import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { GridApi } from 'ag-grid-community'
import store, { AllState } from '../../../../store'
import { featureFlagService } from '../../../../utils/feature-flag'
import {
  getWbsItemCriticalPaths,
  saveWbsItemCriticalPaths,
  WbsItemCriticalPathResponse,
} from '../../../../infrastructure/api/wbs-item-critical-paths'
import { successDummyResponse, APIResponse } from '../../../../lib/commons/api'
import { CriticalPathLinkData } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/GanttCriticalPath/CriticalPathContext'
import { CalendarDateVO } from '../../../../domain/value-object/CalendarDateVO'
import { formatDateForApi, formatDateTime } from '../../../../utils/date'
import { requireSave } from '../../../../store/requiredSaveData'
import { WbsItemLinkSummary } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/WbsItemLink/WbsItemLinkDialog'
import { WbsItemInfo } from '../../../containers/commons/AgGrid/components/cell/custom/gantt/CellRenderer/types'
import { ProjectPlanNewRow } from '../projectPlanNew'

const getNameFromGrid = (api: GridApi, rowIndex: number): string => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return ''
  return (
    (rowNode.data as ProjectPlanNewRow | undefined)?.body?.wbsItem
      ?.displayName ?? ''
  )
}

const getIconUrlFromGrid = (
  api: GridApi,
  rowIndex: number
): string | undefined => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return undefined
  return (rowNode.data as ProjectPlanNewRow | undefined)?.body?.wbsItem
    ?.wbsItemType?.iconUrl
}

const getScheduledDateFromGrid = (
  api: GridApi,
  rowIndex: number
): { startDate?: string; endDate?: string } | undefined => {
  const rowNode = api.getDisplayedRowAtIndex(rowIndex)
  if (!rowNode) return undefined
  const scheduledDate = (rowNode.data as ProjectPlanNewRow | undefined)?.body
    ?.wbsItem?.scheduledDate
  if (!scheduledDate) return undefined
  return {
    startDate: scheduledDate.startDate,
    endDate: scheduledDate.endDate,
  }
}

export const useWbsItemCriticalPathData = () => {
  const projectUuid = useSelector((state: AllState) => state.project.selected)
  const isEnabled = featureFlagService.isEnabled(
    'gantt-dependency-critical-path'
  )

  const [criticalPaths, setCriticalPaths] = useState<CriticalPathLinkData[]>([])
  const [dialogItems, setDialogItems] = useState<WbsItemLinkSummary[]>([])
  const serverDataRef = useRef<WbsItemCriticalPathResponse[]>([])
  const currentLinksRef = useRef<CriticalPathLinkData[]>([])
  const dialogItemsRef = useRef<WbsItemLinkSummary[]>([])
  const gridApiRef = useRef<GridApi | null>(null)

  const setGridApi = useCallback((api: GridApi) => {
    gridApiRef.current = api
  }, [])

  const getGridApi = useCallback(() => gridApiRef.current, [])

  // Actual implementation - recreated when deps change
  const fetchCriticalPathsImpl = useCallback(
    async (uuidToRowIndex: ReadonlyMap<string, number>) => {
      const api = gridApiRef.current
      if (!projectUuid || !isEnabled || !api) return
      const response = await getWbsItemCriticalPaths()
      if (response.hasError) return
      const serverData: WbsItemCriticalPathResponse[] = response.json ?? []
      serverDataRef.current = serverData

      // Include all links, using rowIndex: -1 for non-visible endpoints
      // (e.g. items in collapsed groups). categorizeLinks in GanttBackground
      // will separate visible vs fallback links for rendering.
      const links: CriticalPathLinkData[] = serverData.map(s => ({
        uuid: s.uuid,
        fromRowIndex: uuidToRowIndex.get(s.fromWbsItemUuid) ?? -1,
        toRowIndex: uuidToRowIndex.get(s.toWbsItemUuid) ?? -1,
        fromDate: CalendarDateVO.of(s.fromDate),
        toDate: CalendarDateVO.of(s.toDate),
        fromWbsItemUuid: s.fromWbsItemUuid,
        toWbsItemUuid: s.toWbsItemUuid,
        fromProjectPlanUuid: s.fromProjectPlanUuid,
        toProjectPlanUuid: s.toProjectPlanUuid,
        ...(s.color != null && { color: s.color }),
        ...(s.description != null && { description: s.description }),
        createdAt: formatDateTime(s.createdAt),
        createdBy: s.createdBy,
        updatedAt: formatDateTime(s.updatedAt),
        updatedBy: s.updatedBy,
      }))

      setCriticalPaths(links)
      currentLinksRef.current = links

      // Populate dialog items from ALL server data (with names from API)
      const allDialogItems: WbsItemLinkSummary[] = serverData.map(s => {
        const fromRowIndex = uuidToRowIndex.get(s.fromWbsItemUuid)
        const toRowIndex = uuidToRowIndex.get(s.toWbsItemUuid)
        return {
          uuid: s.uuid,
          fromWbsItemUuid: s.fromWbsItemUuid,
          toWbsItemUuid: s.toWbsItemUuid,
          fromName: s.fromWbsItem.displayName,
          toName: s.toWbsItem.displayName,
          fromIconUrl: s.fromWbsItem.typeDto?.iconUrl,
          toIconUrl: s.toWbsItem.typeDto?.iconUrl,
          fromDate: s.fromDate,
          toDate: s.toDate,
          fromScheduledDate:
            fromRowIndex !== undefined
              ? getScheduledDateFromGrid(api, fromRowIndex)
              : undefined,
          toScheduledDate:
            toRowIndex !== undefined
              ? getScheduledDateFromGrid(api, toRowIndex)
              : undefined,
          color: s.color,
          description: s.description,
          createdAt: formatDateTime(s.createdAt),
          createdBy: s.createdBy,
          updatedAt: formatDateTime(s.updatedAt),
          updatedBy: s.updatedBy,
        }
      })
      dialogItemsRef.current = allDialogItems
      setDialogItems(allDialogItems)
    },
    [projectUuid, isEnabled]
  )

  // Stable ref that always points to the latest implementation
  const fetchImplRef = useRef(fetchCriticalPathsImpl)
  fetchImplRef.current = fetchCriticalPathsImpl

  // Stable function reference - never stale in closures
  const fetchCriticalPaths = useCallback(
    (uuidToRowIndex: ReadonlyMap<string, number>) =>
      fetchImplRef.current(uuidToRowIndex),
    []
  )

  const onCriticalPathsChange = useCallback((links: CriticalPathLinkData[]) => {
    const prevLinks = currentLinksRef.current
    currentLinksRef.current = links

    // Sync changes to dialogItems
    setDialogItems(prev => {
      let updated = prev

      // Handle items removed via gantt
      const removedUuids = new Set(
        prevLinks
          .filter(p => !links.some(l => l.uuid === p.uuid))
          .map(p => p.uuid)
      )
      if (removedUuids.size > 0) {
        updated = updated.filter(item => !removedUuids.has(item.uuid))
      }

      // Handle items added via gantt
      const newItems = links.filter(l => !prev.some(p => p.uuid === l.uuid))
      if (newItems.length > 0) {
        const api = gridApiRef.current
        const newDialogItems: WbsItemLinkSummary[] = newItems.map(l => ({
          uuid: l.uuid,
          fromWbsItemUuid: l.fromWbsItemUuid,
          toWbsItemUuid: l.toWbsItemUuid,
          fromName: api
            ? getNameFromGrid(api, l.fromRowIndex)
            : l.fromWbsItemUuid,
          toName: api ? getNameFromGrid(api, l.toRowIndex) : l.toWbsItemUuid,
          fromIconUrl: api
            ? getIconUrlFromGrid(api, l.fromRowIndex)
            : undefined,
          toIconUrl: api ? getIconUrlFromGrid(api, l.toRowIndex) : undefined,
          fromDate: formatDateForApi(l.fromDate.date),
          toDate: formatDateForApi(l.toDate.date),
          fromScheduledDate: api
            ? getScheduledDateFromGrid(api, l.fromRowIndex)
            : undefined,
          toScheduledDate: api
            ? getScheduledDateFromGrid(api, l.toRowIndex)
            : undefined,
          color: l.color ?? null,
          description: l.description ?? null,
        }))
        updated = [...updated, ...newDialogItems]
      }

      // Sync color/description/date changes
      const linksMap = new Map(links.map(l => [l.uuid, l]))
      updated = updated.map(item => {
        const link = linksMap.get(item.uuid)
        if (!link) return item
        const newColor = link.color ?? null
        const newDesc = link.description ?? null
        const newFromDate = formatDateForApi(link.fromDate.date)
        const newToDate = formatDateForApi(link.toDate.date)
        if (
          newColor === item.color &&
          newDesc === item.description &&
          newFromDate === item.fromDate &&
          newToDate === item.toDate
        ) {
          return item
        }
        return {
          ...item,
          color: newColor,
          description: newDesc,
          fromDate: newFromDate,
          toDate: newToDate,
        }
      })

      dialogItemsRef.current = updated
      return updated
    })

    // requireSave check
    // All links (including non-visible with rowIndex: -1) are now in Context,
    // so no need to exclude non-visible items from deletion detection.
    const server = serverDataRef.current
    const hasAddedOrDeleted =
      links.some(l => !server.some(s => s.uuid === l.uuid)) ||
      server.some(s => !links.some(l => l.uuid === s.uuid))
    const hasFieldChanges = links.some(l => {
      const s = server.find(s => s.uuid === l.uuid)
      if (!s) return false
      return (
        formatDateForApi(l.fromDate.date) !== s.fromDate ||
        formatDateForApi(l.toDate.date) !== s.toDate ||
        (l.color ?? null) !== (s.color ?? null) ||
        (l.description ?? null) !== (s.description ?? null)
      )
    })
    if (hasAddedOrDeleted || hasFieldChanges) {
      store.dispatch(requireSave())
    }
  }, [])

  const removeByWbsItemUuids = useCallback((uuids: Set<string>) => {
    const filtered = currentLinksRef.current.filter(
      l => !uuids.has(l.fromWbsItemUuid) && !uuids.has(l.toWbsItemUuid)
    )
    if (filtered.length !== currentLinksRef.current.length) {
      currentLinksRef.current = filtered
      setCriticalPaths(filtered)

      // Also remove from dialogItems
      setDialogItems(prev => {
        const next = prev.filter(
          item =>
            !uuids.has(item.fromWbsItemUuid) && !uuids.has(item.toWbsItemUuid)
        )
        dialogItemsRef.current = next
        return next
      })

      store.dispatch(requireSave())
    }
  }, [])

  const updateLinkFromDialog = useCallback(
    (
      uuid: string,
      updates: {
        color?: string
        description?: string
        fromDate?: string
        toDate?: string
      }
    ) => {
      // Update dialogItems
      setDialogItems(prev => {
        const next = prev.map(item =>
          item.uuid === uuid
            ? {
                ...item,
                ...(updates.color !== undefined && {
                  color: updates.color ?? null,
                }),
                ...(updates.description !== undefined && {
                  description: updates.description ?? null,
                }),
                ...(updates.fromDate !== undefined && {
                  fromDate: updates.fromDate,
                }),
                ...(updates.toDate !== undefined && {
                  toDate: updates.toDate,
                }),
              }
            : item
        )
        dialogItemsRef.current = next
        return next
      })

      // Build link updates (color, description, and date as CalendarDateVO)
      const linkUpdates: Partial<CriticalPathLinkData> = {}
      if (updates.color !== undefined) {
        linkUpdates.color = updates.color
      }
      if (updates.description !== undefined) {
        linkUpdates.description = updates.description
      }
      if (updates.fromDate !== undefined) {
        linkUpdates.fromDate = CalendarDateVO.of(updates.fromDate)
      }
      if (updates.toDate !== undefined) {
        linkUpdates.toDate = CalendarDateVO.of(updates.toDate)
      }

      // Update currentLinksRef and criticalPaths state (for visible items)
      currentLinksRef.current = currentLinksRef.current.map(l =>
        l.uuid === uuid ? { ...l, ...linkUpdates } : l
      )
      setCriticalPaths(prev =>
        prev.map(l => (l.uuid === uuid ? { ...l, ...linkUpdates } : l))
      )

      store.dispatch(requireSave())
    },
    []
  )

  const removeLinkFromDialog = useCallback((uuid: string) => {
    // Update dialogItems
    setDialogItems(prev => {
      const next = prev.filter(item => item.uuid !== uuid)
      dialogItemsRef.current = next
      return next
    })

    // Update currentLinksRef and criticalPaths state
    currentLinksRef.current = currentLinksRef.current.filter(
      l => l.uuid !== uuid
    )
    setCriticalPaths(prev => prev.filter(l => l.uuid !== uuid))

    store.dispatch(requireSave())
  }, [])

  const saveCriticalPaths = useCallback(async (): Promise<APIResponse> => {
    if (!isEnabled) return successDummyResponse

    const current = currentLinksRef.current
    const allDialogCurrent = dialogItemsRef.current
    const server = serverDataRef.current

    const added = current
      .filter(l => !server.some(s => s.uuid === l.uuid))
      .map(l => ({
        uuid: l.uuid,
        fromWbsItemUuid: l.fromWbsItemUuid,
        fromDate: formatDateForApi(l.fromDate.date) ?? '',
        toWbsItemUuid: l.toWbsItemUuid,
        toDate: formatDateForApi(l.toDate.date) ?? '',
        color: l.color,
        description: l.description,
      }))

    // Visible items that were edited
    const editedFromContext = current
      .filter(l => {
        const s = server.find(s => s.uuid === l.uuid)
        if (!s) return false
        return (
          formatDateForApi(l.fromDate.date) !== s.fromDate ||
          formatDateForApi(l.toDate.date) !== s.toDate ||
          (l.color ?? null) !== (s.color ?? null) ||
          (l.description ?? null) !== (s.description ?? null)
        )
      })
      .map(l => ({
        uuid: l.uuid,
        fromDate: formatDateForApi(l.fromDate.date) ?? '',
        toDate: formatDateForApi(l.toDate.date) ?? '',
        color: l.color,
        description: l.description,
      }))

    // Non-visible items that were edited via dialog
    const editedFromDialog = allDialogCurrent
      .filter(d => {
        if (current.some(c => c.uuid === d.uuid)) return false
        const s = server.find(s => s.uuid === d.uuid)
        if (!s) return false
        return (
          (d.color ?? null) !== (s.color ?? null) ||
          (d.description ?? null) !== (s.description ?? null) ||
          (d.fromDate ?? null) !== (s.fromDate ?? null) ||
          (d.toDate ?? null) !== (s.toDate ?? null)
        )
      })
      .map(d => {
        const s = server.find(s => s.uuid === d.uuid)!
        return {
          uuid: d.uuid,
          fromDate: d.fromDate ?? s.fromDate,
          toDate: d.toDate ?? s.toDate,
          color: d.color ?? undefined,
          description: d.description ?? undefined,
        }
      })

    const edited = [...editedFromContext, ...editedFromDialog]

    // Use both currentLinks and dialogItems as authoritative sources for what should exist
    const allCurrentUuids = new Set([
      ...current.map(l => l.uuid),
      ...allDialogCurrent.map(d => d.uuid),
    ])
    const deleted = server
      .filter(s => !allCurrentUuids.has(s.uuid))
      .map(s => ({ uuid: s.uuid }))

    if (!added.length && !edited.length && !deleted.length) {
      return successDummyResponse
    }

    const response = await saveWbsItemCriticalPaths({ added, edited, deleted })
    if (!response.hasError) {
      // Rebuild serverDataRef from all current items
      const newServer: WbsItemCriticalPathResponse[] = []

      // Visible items from currentLinksRef
      for (const l of current) {
        const existing = server.find(s => s.uuid === l.uuid)
        newServer.push({
          uuid: l.uuid,
          projectUuid: existing?.projectUuid ?? '',
          fromWbsItemUuid: l.fromWbsItemUuid,
          fromProjectPlanUuid:
            l.fromProjectPlanUuid ?? existing?.fromProjectPlanUuid ?? '',
          fromDate: formatDateForApi(l.fromDate.date) ?? '',
          toWbsItemUuid: l.toWbsItemUuid,
          toProjectPlanUuid:
            l.toProjectPlanUuid ?? existing?.toProjectPlanUuid ?? '',
          toDate: formatDateForApi(l.toDate.date) ?? '',
          color: l.color ?? null,
          description: l.description ?? null,
          lockVersion: existing?.lockVersion ?? 0,
          fromWbsItem: existing?.fromWbsItem ?? ({} as never),
          toWbsItem: existing?.toWbsItem ?? ({} as never),
          createdAt: existing?.createdAt ?? 0,
          createdBy: existing?.createdBy ?? ({} as never),
          updatedAt: existing?.updatedAt ?? 0,
          updatedBy: existing?.updatedBy ?? ({} as never),
        })
      }

      // Non-visible items still in dialogItems
      for (const d of allDialogCurrent) {
        if (current.some(c => c.uuid === d.uuid)) continue
        const existing = server.find(s => s.uuid === d.uuid)
        if (!existing) continue
        newServer.push({
          ...existing,
          fromDate: d.fromDate ?? existing.fromDate,
          toDate: d.toDate ?? existing.toDate,
          color: d.color,
          description: d.description,
        })
      }

      serverDataRef.current = newServer
    }
    return response
  }, [isEnabled])

  // Build wbsItemUuid → info map from dialogItems (server-sourced display names/icons)
  const wbsItemInfoMap = useMemo(() => {
    const map = new Map<string, WbsItemInfo>()
    for (const item of dialogItems) {
      map.set(item.fromWbsItemUuid, {
        displayName: item.fromName,
        iconUrl: item.fromIconUrl ?? undefined,
      })
      map.set(item.toWbsItemUuid, {
        displayName: item.toName,
        iconUrl: item.toIconUrl ?? undefined,
      })
    }
    return map
  }, [dialogItems])

  const hasUnsavedChanges = useCallback((): boolean => {
    if (!isEnabled) return false
    const current = currentLinksRef.current
    const allDialog = dialogItemsRef.current
    const server = serverDataRef.current

    // Check for added items
    if (current.some(l => !server.some(s => s.uuid === l.uuid))) return true

    // Check for deleted items
    const allCurrentUuids = new Set([
      ...current.map(l => l.uuid),
      ...allDialog.map(d => d.uuid),
    ])
    if (server.some(s => !allCurrentUuids.has(s.uuid))) return true

    // Check for edited items (visible)
    if (
      current.some(l => {
        const s = server.find(s => s.uuid === l.uuid)
        if (!s) return false
        return (
          formatDateForApi(l.fromDate.date) !== s.fromDate ||
          formatDateForApi(l.toDate.date) !== s.toDate ||
          (l.color ?? null) !== (s.color ?? null) ||
          (l.description ?? null) !== (s.description ?? null)
        )
      })
    ) {
      return true
    }

    // Check for edited items (non-visible, via dialog)
    if (
      allDialog.some(d => {
        if (current.some(c => c.uuid === d.uuid)) return false
        const s = server.find(s => s.uuid === d.uuid)
        if (!s) return false
        return (
          (d.color ?? null) !== (s.color ?? null) ||
          (d.description ?? null) !== (s.description ?? null) ||
          (d.fromDate ?? null) !== (s.fromDate ?? null) ||
          (d.toDate ?? null) !== (s.toDate ?? null)
        )
      })
    ) {
      return true
    }

    return false
  }, [isEnabled])

  return {
    criticalPaths,
    onCriticalPathsChange,
    fetchCriticalPaths,
    saveCriticalPaths,
    removeByWbsItemUuids,
    setGridApi,
    getGridApi,
    isEnabled,
    dialogItems,
    updateLinkFromDialog,
    removeLinkFromDialog,
    wbsItemInfoMap,
    hasUnsavedChanges,
  }
}
